import React, { useState } from 'react';
import { Flower2, CheckCircle, Flame, Layers, Clock, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const YOGA_DATA = [
  { id: 1, flow: 'Sun Salutation', poses: 12, intensity: 'Low', duration: '15 min', color: 'from-emerald-500 to-teal-600' },
  { id: 2, flow: 'Vinyasa Flow', poses: 24, intensity: 'Medium', duration: '30 min', color: 'from-emerald-600 to-emerald-800' },
  { id: 3, flow: 'Power Yoga', poses: 32, intensity: 'High', duration: '45 min', color: 'from-teal-600 to-cyan-700' },
  { id: 4, flow: 'Restorative', poses: 8, intensity: 'Low', duration: '20 min', color: 'from-emerald-400 to-emerald-600' },
];

const YogaSection = () => {
  const [filter, setFilter] = useState('All');
  
  const filteredData = filter === 'All' 
    ? YOGA_DATA 
    : YOGA_DATA.filter(item => item.intensity === filter);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-1">
        <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-[40px] border-2 border-dashed border-gray-100">
  {/* Pulsing Icon */}
  <div className="relative mb-6">
    <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
    <div className="relative bg-emerald-500 p-5 rounded-full text-white shadow-xl shadow-emerald-500/20">
      <Sparkles size={32} className="animate-pulse" />
    </div>
  </div>

  <h3 className="text-2xl font-black text-gray-900 tracking-tighter italic text-center mb-2">
    Engineering <span className="text-emerald-500">Excellence</span>
  </h3>
  
  <p className="text-gray-400 font-bold text-[10px] uppercase tracking-[0.3em] text-center max-w-xs leading-relaxed">
    We are currently developing this module to enhance your fitness journey.
  </p>

  <div className="mt-8 flex items-center gap-2">
    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" />
  </div>
</div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Yoga Practice</h2>
          <p className="text-sm text-gray-500">Find your balance and strength</p>
        </div>

        {/* Intensity Filter */}
        <div className="flex bg-gray-100 p-1 rounded-xl w-fit">
          {['All', 'Low', 'Medium', 'High'].map((level) => (
            <button
              key={level}
              onClick={() => setFilter(level)}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                filter === level ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Responsive Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredData.map((item) => (
          <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            key={item.id}
            className={`group relative overflow-hidden bg-gradient-to-br ${item.color} rounded-[2.5rem] p-8 text-white shadow-xl hover:shadow-emerald-200/50 transition-all`}
          >
            {/* Animated Background Icon */}
            <Flower2 className="absolute -right-6 -bottom-6 w-40 h-40 opacity-10 group-hover:rotate-90 group-hover:scale-110 transition-transform duration-700" />
            
            <div className="relative z-10 flex flex-col h-full justify-between">
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div className="bg-white/20 backdrop-blur-md p-3 rounded-2xl">
                    <Flower2 size={24} />
                  </div>
                  <div className="flex gap-2">
                    <span className="bg-black/10 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                      <Flame size={10} /> {item.intensity}
                    </span>
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-1 tracking-tight">{item.flow}</h3>
                
                <div className="flex items-center gap-4 mt-4 text-emerald-50/80 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Layers size={14} />
                    <span>{item.poses} Poses</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock size={14} />
                    <span>{item.duration}</span>
                  </div>
                </div>
              </div>

              <button className="mt-8 bg-white text-emerald-700 w-full py-4 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-emerald-50 active:scale-95 transition-all shadow-lg">
                <CheckCircle size={18} /> Start Session
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
          <p className="text-gray-400 font-medium">No sessions found for this intensity.</p>
        </div>
      )}
    </div>
  );
};

export default YogaSection;